import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import './Semesters.css';

const Semesters = () => {
    const { t, i18n } = useTranslation();
    const [semesters, setSemesters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [form, setForm] = useState({ hijriYear: '', term: '' });
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState(null);
    const [modalError, setModalError] = useState(null);

    const isAr = i18n.language === 'ar';

    const getErrorMessage = (errorResponse) => {
        const msg = errorResponse?.data?.message;
        if (!msg) return t('common.error');
        
        switch (msg) {
            case 'السنة الهجرية ورقم الفصل مطلوبان (1 أو 2)':
                return t('admin.semesters.errors.missingFields');
            case 'هذا الفصل الدراسي موجود بالفعل':
                return t('admin.semesters.errors.alreadyExists');
            case 'الفصل الدراسي غير موجود':
                return t('admin.semesters.errors.notFound');
            case 'لا يمكن حذف الفصل الدراسي المفعّل. قم بتفعيل فصل آخر أولاً.':
                return t('admin.semesters.errors.cannotDeleteActive');
            case 'لا يمكن حذف الفصل الدراسي الوحيد':
                return t('admin.semesters.errors.cannotDeleteOnly');
            case 'حدث خطأ في الخادم':
                return t('admin.semesters.errors.serverError');
            default:
                return msg;
        }
    };

    useEffect(() => {
        fetchSemesters();
    }, []);

    const fetchSemesters = async () => {
        try {
            const response = await api.get('/semesters');
            if (response.data.success) {
                setSemesters(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching semesters:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!form.hijriYear || !form.term) return;

        setSubmitting(true);
        try {
            const response = await api.post('/semesters', {
                hijriYear: parseInt(form.hijriYear),
                term: parseInt(form.term)
            });

            if (response.data.success) {
                setMessage({ type: 'success', text: t('admin.semesters.addSuccess') });
                setShowAddModal(false);
                setForm({ hijriYear: '', term: '' });
                fetchSemesters();
            }
        } catch (error) {
            setModalError(getErrorMessage(error.response));
        } finally {
            setSubmitting(false);
        }
    };

    const handleActivate = async (semesterId) => {
        try {
            const response = await api.put(`/semesters/${semesterId}/activate`);
            if (response.data.success) {
                setMessage({ type: 'success', text: t('admin.semesters.activateSuccess') });
                fetchSemesters();
                // Reload after a short delay to update TopBar
                setTimeout(() => window.location.reload(), 800);
            }
        } catch (error) {
            setMessage({
                type: 'error',
                text: getErrorMessage(error.response)
            });
        }
    };

    const handleDelete = async (semester) => {
        if (semester.is_active) {
            setMessage({ type: 'error', text: t('admin.semesters.cannotDeleteActive') });
            return;
        }

        if (!window.confirm(t('admin.semesters.deleteConfirm'))) return;

        try {
            const response = await api.delete(`/semesters/${semester.semester_id}`);
            if (response.data.success) {
                setMessage({ type: 'success', text: t('admin.semesters.deleteSuccess') });
                fetchSemesters();
            }
        } catch (error) {
            setMessage({
                type: 'error',
                text: getErrorMessage(error.response)
            });
        }
    };

    // Clear message after 4 seconds
    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => setMessage(null), 4000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>{t('common.loading')}</p>
            </div>
        );
    }

    return (
        <div className="semesters-page fade-in">
            <div className="page-header">
                <h1>{t('admin.semesters.title')}</h1>
                <button
                    className="btn-primary"
                    onClick={() => {
                        setForm({ hijriYear: '', term: '' });
                        setModalError(null);
                        setShowAddModal(true);
                    }}
                >
                    <span className="material-icons">add</span>
                    {t('admin.semesters.addSemester')}
                </button>
            </div>

            {message && (
                <div className={`alert alert-${message.type}`}>
                    <span className="material-icons">
                        {message.type === 'success' ? 'check_circle' : 'error'}
                    </span>
                    {message.text}
                </div>
            )}

            {semesters.length === 0 ? (
                <div className="empty-state">
                    <span className="material-icons">date_range</span>
                    <p>{t('admin.semesters.noSemesters')}</p>
                </div>
            ) : (
                <div className="semesters-grid">
                    {semesters.map((sem) => (
                        <div
                            key={sem.semester_id}
                            className={`semester-card ${sem.is_active ? 'active' : ''}`}
                        >
                            <div className="semester-card-header">
                                <div className="semester-icon">
                                    <span className="material-icons">
                                        {sem.term === 1 ? 'looks_one' : 'looks_two'}
                                    </span>
                                </div>
                                <div className="semester-title-area">
                                    <h3>{isAr ? sem.label_ar : sem.label_en}</h3>
                                </div>
                                {!!sem.is_active && (
                                    <div className="active-badge">
                                        <span className="material-icons">check_circle</span>
                                        {t('admin.semesters.active')}
                                    </div>
                                )}
                            </div>

                            <div className="semester-stats">
                                <div className="stat-item">
                                    <span className="material-icons">menu_book</span>
                                    <span className="stat-value">{sem.courses_count || 0}</span>
                                    <span className="stat-label">{t('admin.semesters.coursesCount')}</span>
                                </div>
                                <div className="stat-item">
                                    <span className="material-icons">people</span>
                                    <span className="stat-value">{sem.students_count || 0}</span>
                                    <span className="stat-label">{t('admin.semesters.studentsCount')}</span>
                                </div>
                            </div>

                            <div className="semester-actions">
                                {!sem.is_active && (
                                    <button
                                        className="btn-activate"
                                        onClick={() => handleActivate(sem.semester_id)}
                                        title={t('admin.semesters.activate')}
                                    >
                                        <span className="material-icons">check_circle_outline</span>
                                        {t('admin.semesters.activate')}
                                    </button>
                                )}
                                {!sem.is_active && (
                                    <button
                                        className="btn-delete"
                                        onClick={() => handleDelete(sem)}
                                        title={t('common.delete')}
                                    >
                                        <span className="material-icons">delete_outline</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add Semester Modal */}
            {showAddModal && (
                <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{t('admin.semesters.addSemester')}</h2>
                            <button className="modal-close" onClick={() => setShowAddModal(false)}>
                                <span className="material-icons">close</span>
                            </button>
                        </div>
                        <form onSubmit={handleCreate}>
                            {modalError && (
                                <div className="alert alert-error" style={{ marginBottom: '20px' }}>
                                    <span className="material-icons">error</span>
                                    {modalError}
                                </div>
                            )}
                            <div className="form-group">
                                <label>{t('admin.semesters.hijriYear')}</label>
                                <input
                                    type="number"
                                    value={form.hijriYear}
                                    onChange={(e) => setForm({ ...form, hijriYear: e.target.value })}
                                    placeholder=""
                                    min="1400"
                                    max="1500"
                                    required
                                    className="form-input"
                                />
                            </div>
                            <div className="form-group">
                                <label>{t('admin.semesters.term')}</label>
                                <select
                                    value={form.term}
                                    onChange={(e) => setForm({ ...form, term: e.target.value })}
                                    required
                                    className="form-input"
                                >
                                    <option value="">{t('admin.semesters.selectTerm')}</option>
                                    <option value="1">{t('admin.semesters.firstTerm')}</option>
                                    <option value="2">{t('admin.semesters.secondTerm')}</option>
                                </select>
                            </div>
                            <div className="modal-actions">
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => setShowAddModal(false)}
                                >
                                    {t('common.cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="btn-primary"
                                    disabled={submitting}
                                >
                                    {submitting ? t('common.loading') : t('common.add')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Semesters;
